/* istanbul ignore file */
import {
  KatalMonitoring,
  LogLevel,
  AppType,
  MetricDestinationSink,
} from '@amzn/katal-monitoring';
import { KatalMonitoringDriver } from '@amzn/katal-monitoring-aws-driver';
import { MONS_ROUTE_BASE } from './constants';

const IS_BEHIND_MONS = !SERVER_MODE || SERVER_MODE === 'mons';

// Mons injects meta tags into the page head with request context.
// @see https://code.amazon.com/packages/MonsControlPlane/blobs/mainline/--/src/com/amazon/mons/control/html/SellerCentralLayoutHtmlResponseBuilder.java
const MONS_REQUEST_ID_META_TAG = 'a2z:request_id';

const getMetaContent = (name: string): string | undefined =>
  document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;

// The Mons route path for the monitoring API Gateway endpoint.
// This value is injected at deploy time by the CDK deployment lambda via KatalMonitoringUrlMons.
// Update this path if your Mons monitoring route is configured differently in Monster UI.
const MONITORING_URL_PATH = '/app-monitoring';

const shouldLogToConsole = () =>
  process.env.NODE_ENV !== 'test' && process.env.NODE_ENV !== 'production';

// Prevent monitoring from making requests when not behind Mons.
const monitoringUrl = IS_BEHIND_MONS
  ? `${MONS_ROUTE_BASE}${MONITORING_URL_PATH}`
  : undefined;

const monitoringBackendApiOverride = IS_BEHIND_MONS
  ? undefined
  : () => Promise.resolve();

// Prevent console errors when outside of Mons.
const metricContext = IS_BEHIND_MONS
  ? undefined
  : {
      site: 'unknown',
      serviceName: 'HILTBusinessReportingUIWebsite',
    };

const getMonitor = (): KatalMonitoring => {
  return new KatalMonitoring({
    monitoringDriver: new KatalMonitoringDriver({
      url: monitoringUrl,
      logToConsole: shouldLogToConsole(),
      backendApiOverride: monitoringBackendApiOverride,
    }),
    appType: IS_BEHIND_MONS ? AppType.MONS : AppType.NON_MONS,
    logThreshold: LogLevel.info,
    destinationSinks: [
      MetricDestinationSink.CLOUDWATCH,
      MetricDestinationSink.SUSHI,
    ],
    metricContext,
  });
};

const monitor: KatalMonitoring = getMonitor();
monitor.addErrorListener();

const METRIC_NAMES = {
  APP_INITIALIZED: 'App.Initialized',
  ROOT_ERROR_BOUNDARY_ENCOUNTER: 'RootErrorBoundary.Encounter',
};

const LOG_PREFIX = '[hilt_business_reporting_ui_website]';

let requestId: string | undefined;

const logError = (
  message: string,
  context?: Record<string, unknown> | undefined,
) => {
  if (!requestId) {
    // Add MONS request ID as context to aid in debugging.
    requestId = getMetaContent(MONS_REQUEST_ID_META_TAG);
  }
  monitor.error(`${LOG_PREFIX}: ${message}`, {
    ...(requestId && { requestId }),
    ...context,
  });
};

export { monitor, logError, METRIC_NAMES, LOG_PREFIX };
